import React, { useState, useEffect } from 'react';
import { Plus, Link, ChevronRight, ArrowLeft, LoaderCircle, Check } from 'lucide-react';
import { useLang } from './i18n';

export function CommunityDialog({
  request,
  onDone
}: {
  request: (url: string, method?: string, body?: unknown) => Promise<any>;
  onDone: (id: string) => Promise<void>;
}) {
  const [, , tr] = useLang();
  const initialInvite = new URLSearchParams(location.search).get('invite') || '';
  const [step, setStep] = useState(initialInvite ? 'join' : 'choice');
  const [value, setValue] = useState(initialInvite);
  const [template, setTemplate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<any>(null);
  // Only a link from the address bar, a pasted link or the Join button opens the invitation; typing a code never does.
  const [reviewing, setReviewing] = useState(!!initialInvite);
  const inviteCode =
    value
      .trim()
      .split('invite=')
      .at(-1)
      ?.split(/[?&#]/)[0]
      .replace(/\/+$/, '')
      .split('/')
      .at(-1) || '';

  useEffect(() => {
    if (step !== 'join' || inviteCode.length < 3) {
      setPreview(null);
      return;
    }
    const c = new AbortController();
    fetch('/api/invites/' + encodeURIComponent(inviteCode), { signal: c.signal })
      .then(r => (r.ok ? r.json() : null))
      .then(setPreview)
      .catch(() => {});
    return () => c.abort();
  }, [step, inviteCode]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (step === 'join' && preview && !reviewing) {
      setReviewing(true);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const d = await request(
        step === 'create' ? '/communities' : '/communities/join',
        'POST',
        step === 'create' ? { name: value, template: template.trim() || undefined } : { invite: inviteCode || value }
      );
      history.replaceState({}, '', location.pathname);
      await onDone(d.id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  if (step === 'join' && preview && reviewing) {
    const iconSrc =
      preview.icon === 'icon.png'
        ? '/icon.png'
        : preview.icon
        ? '/api/files/' + preview.icon
        : null;
    return (
      <div className="join-server-dialog">
        <div className="join-server-icon">
          {iconSrc ? <img src={iconSrc} alt={preview.name} /> : preview.name.slice(0, 1)}
        </div>
        <p>{tr("You've been invited to join", 'Du wurdest eingeladen, beizutreten')}</p>
        <h2 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {preview.name}
          {(preview.verified || preview.id === 'malvo-official') && (
            <span className="verified-check-sm">
              <Check size={11} strokeWidth={3} />
            </span>
          )}
        </h2>
        <p className="join-count">
          <i />
          {preview.online} online <span>•</span> {preview.members} {tr('Members', 'Mitglieder')}
        </p>
        <form onSubmit={submit}>
          <button className="primary" disabled={busy}>
            {busy
              ? tr('Connecting…', 'Verbinden …')
              : preview.joined
              ? tr('Open Server', 'Server öffnen')
              : tr('Join Server', 'Server beitreten')}
          </button>
        </form>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button
          className="secondary"
          onClick={() => {
            setValue('');
            setPreview(null);
            setReviewing(false);
          }}
        >
          {tr('Enter another invite', 'Andere Einladung')}
        </button>
      </div>
    );
  }

  return (
    <div key={step} className="community-dialog-content">
      {step === 'choice' ? (
        <>
          <h2>{tr('Add a Server', 'Server hinzufügen')}</h2>
          <p>
            {tr(
              'Create your own community or join an existing server.',
              'Erstelle deinen eigenen Ort oder tritt einem Server bei.'
            )}
          </p>
          <div className="community-choices">
            <button
              onClick={() => {
                setValue('');
                setStep('create');
              }}
            >
              <span>
                <Plus size={21} />
              </span>
              <div>
                <b>{tr('Create a Server', 'Server erstellen')}</b>
                <small>{tr('With text channels and voice rooms', 'Mit Textkanälen und Sprachräumen')}</small>
              </div>
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => {
                setValue('');
                setStep('join');
              }}
            >
              <span>
                <Link size={21} />
              </span>
              <div>
                <b>{tr('Join with an Invite', 'Mit Einladung beitreten')}</b>
                <small>{tr('Enter an invite link or code', 'Einladungslink oder Code eingeben')}</small>
              </div>
              <ChevronRight size={18} />
            </button>
          </div>
        </>
      ) : (
        <>
          <button
            className="dialog-back"
            onClick={() => {
              setStep('choice');
              setError('');
            }}
          >
            <ArrowLeft size={14} />
            {tr('Back', 'Zurück')}
          </button>
          <h2>{step === 'create' ? tr('Create a Server', 'Server erstellen') : tr('Join a Server', 'Server beitreten')}</h2>
          <p>
            {step === 'create'
              ? tr(
                  'Choose a name for your server. You can customize channels and roles anytime.',
                  'Wähle einen Namen. Du kannst danach Kanäle anlegen und Freunde einladen.'
                )
              : tr(
                  'Enter a MALVO invite link or code (e.g. malvo.eu/malvo).',
                  'Füge einen MALVO-Einladungslink oder Code ein (z. B. https://malvo.eu/malvo).'
                )}
          </p>
          <form className="form-stack" onSubmit={submit}>
            <fieldset disabled={busy}>
              <label>
                {step === 'create' ? tr('Server Name', 'Name') : tr('Invite Link or Code', 'Einladung')}
                <input
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  onPaste={e => {if (step === 'join' && /[/=]/.test(e.clipboardData.getData('text'))) setReviewing(true);}}
                  autoFocus
                  required={step !== 'create' || !template.trim()}
                  minLength={step === 'create' ? 2 : 3}
                  maxLength={step === 'create' ? 48 : 250}
                  placeholder={
                    step === 'create'
                      ? tr('Your Server Name', 'Dein Server')
                      : tr('malvo.eu/invite/CODE', 'Einladung oder Code einfügen')
                  }
                />
              </label>
              {step === 'create' && (
                <label>
                  {tr('Discord template link (optional)', 'Discord-Vorlagenlink (optional)')}
                  <input
                    value={template}
                    onChange={e => setTemplate(e.target.value)}
                    maxLength={200}
                    placeholder="https://discord.new/..."
                  />
                  <small style={{ opacity: .7, fontWeight: 400 }}>
                    {tr(
                      'Copies channels, categories and roles from a Discord server template. Leave the name empty to use the template name.',
                      'Übernimmt Kanäle, Kategorien und Rollen aus einer Discord-Servervorlage. Name leer lassen = Name der Vorlage.'
                    )}
                  </small>
                </label>
              )}
              <button className="primary" disabled={(!value.trim() && !(step === 'create' && template.trim())) || busy}>
                {busy ? (
                  <LoaderCircle size={16} className="spin" />
                ) : step === 'create' ? (
                  template.trim() ? tr('Import from Discord', 'Aus Discord importieren') : tr('Create', 'Erstellen')
                ) : (
                  tr('Join Server', 'Beitreten')
                )}
              </button>
            </fieldset>
          </form>
        </>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
